import React, { useEffect, useRef, useState } from "react";

// Safari (macOS + every iOS browser) decodes WebM but DROPS the alpha channel.
// For those engines we play the server-generated "stacked" MP4 (colour on top,
// alpha mask below) and recompose transparency on a WebGL canvas.
const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator?.platform === "MacIntel" && navigator?.maxTouchPoints > 1);
const isSafari = /AppleWebKit/i.test(ua) && !/Chrome|CriOS|Chromium|Edg|OPR|Firefox|FxiOS|SamsungBrowser/i.test(ua);
const isAndroid = /Android/i.test(ua);
const isCoarse = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
const forceStacked = typeof window !== "undefined" && /[?&]alphaMode=stacked/.test(window.location.search);
// Mobile browsers (iOS WebKit, Android hardware decoders) drop WebM alpha → use the stacked MP4 there.
export const NEEDS_STACKED_ALPHA = isIOS || isSafari || isAndroid || isCoarse || forceStacked;

const stackedUrlFor = (src) => (/\.(webm|mkv|mov)(\?.*)?$/i.test(src) ? src.replace(/\.(webm|mkv|mov)(\?.*)?$/i, "_stacked.mp4") : null);

const VS = `attribute vec2 p; varying vec2 v; void main(){ v = vec2(p.x*0.5+0.5, 0.5-p.y*0.5); gl_Position = vec4(p,0.0,1.0); }`;
const FS = `precision mediump float; uniform sampler2D t; varying vec2 v;
void main(){ vec3 c = texture2D(t, vec2(v.x, v.y*0.5)).rgb; float a = texture2D(t, vec2(v.x, 0.5+v.y*0.5)).r; gl_FragColor = vec4(c*a, a); }`;

function StackedCanvas({ src, poster, loop, playbackRate, className, style, onError, testid }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [ratio, setRatio] = useState(null);

  useEffect(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return undefined;
    const gl = canvas.getContext("webgl", { premultipliedAlpha: true, alpha: true });
    if (!gl) { onError?.(); return undefined; }
    const sh = (type, srcCode) => { const s = gl.createShader(type); gl.shaderSource(s, srcCode); gl.compileShader(s); return s; };
    const prog = gl.createProgram();
    gl.attachShader(prog, sh(gl.VERTEX_SHADER, VS));
    gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, FS));
    gl.linkProgram(prog);
    gl.useProgram(prog);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, "p");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.clearColor(0, 0, 0, 0);

    let raf = 0;
    let alive = true;
    const draw = () => {
      if (!alive) return;
      if (video.readyState >= 2) {
        const w = video.videoWidth, h = video.videoHeight >> 1;
        if (w && h && (canvas.width !== w || canvas.height !== h)) {
          canvas.width = w; canvas.height = h;
          gl.viewport(0, 0, w, h);
          setRatio(`${w} / ${h}`);
        }
        gl.bindTexture(gl.TEXTURE_2D, tex);
        try { gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video); } catch (_) { /* frame not ready */ }
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      }
      if (video.requestVideoFrameCallback) video.requestVideoFrameCallback(draw);
      else raf = requestAnimationFrame(draw);
    };
    draw();
    const tryPlay = () => video.play().catch(() => {});
    tryPlay();
    document.addEventListener("touchstart", tryPlay, { once: true, passive: true });
    return () => { alive = false; cancelAnimationFrame(raf); document.removeEventListener("touchstart", tryPlay); };
  }, [src, onError]);

  return (
    <>
      <video
        ref={videoRef}
        src={src}
        muted
        playsInline
        autoPlay
        loop={loop}
        preload="auto"
        crossOrigin="anonymous"
        onLoadedMetadata={(e) => { e.currentTarget.playbackRate = playbackRate || 1; }}
        onError={onError}
        style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
        aria-hidden
      />
      <canvas
        ref={canvasRef}
        className={className}
        style={{ ...style, aspectRatio: style?.aspectRatio || ratio || undefined, backgroundImage: ratio ? undefined : (poster ? `url(${poster})` : undefined), backgroundSize: "contain", backgroundRepeat: "no-repeat", backgroundPosition: "center" }}
        data-testid={testid}
        data-alpha-mode="stacked"
      />
    </>
  );
}

export function AlphaVideo({ src, poster, loop = true, playbackRate = 1, className, style, onError, onLoadedMetadata, testid }) {
  const [stacked, setStacked] = useState(null); // null = probing, "" = none, url = use it
  const candidate = NEEDS_STACKED_ALPHA ? stackedUrlFor(src) : null;

  useEffect(() => {
    let cancelled = false;
    if (!candidate) { setStacked(""); return undefined; }
    setStacked(null);
    fetch(candidate, { method: "GET", headers: { Range: "bytes=0-0" } })
      .then((r) => { if (!cancelled) setStacked(r.ok ? candidate : ""); })
      .catch(() => { if (!cancelled) setStacked(""); });
    return () => { cancelled = true; };
  }, [candidate]);

  if (candidate && stacked === null) return null; // keep the poster/image until we know
  if (stacked) {
    return <StackedCanvas src={stacked} poster={poster} loop={loop} playbackRate={playbackRate} className={className} style={style} onError={onError} testid={testid} />;
  }
  return (
    <video
      key={src}
      src={src}
      poster={poster || undefined}
      autoPlay
      muted
      loop={loop}
      playsInline
      preload="auto"
      onLoadedMetadata={(e) => { e.currentTarget.playbackRate = playbackRate || 1; onLoadedMetadata?.(e); }}
      onError={onError}
      className={className}
      style={style}
      data-testid={testid}
      data-alpha-mode="native"
    />
  );
}
